import React from 'react';
import { Box, Container, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const termsSections = [
  {
    title: '1. About these Terms',
    paragraphs: [
      'These Terms of Service govern your access to and use of the A3 Research and A3 Learning websites, applications, study resources, research materials, school tools, mock examinations, laboratory simulations, and related services (together, the “Services”). “A3”, “we”, “us”, and “our” refer to the operators of those Services.',
      'By creating an account, accessing, or using the Services, you agree to these Terms and our Privacy Policy. If you do not agree, do not use the Services.',
    ],
  },
  {
    title: '2. Eligibility and student accounts',
    paragraphs: [
      'You must provide accurate information and meet the age and consent requirements that apply where you live. Students who are minors must use the Services with any parent, guardian, or school authorization required by applicable law. A parent, guardian, or school that creates or manages an account for a student is responsible for having authority to do so.',
      'Keep your sign-in credentials private. You are responsible for activity carried out through your account and must promptly tell us if you suspect unauthorized access. Do not share an account in a way that bypasses seat, subscription, or school access controls.',
    ],
  },
  {
    title: '3. The Services and educational content',
    paragraphs: [
      'The Services provide educational, research, administrative, and assessment tools. Content may include lessons, question banks, AI-assisted responses, feedback, simulations, and school records. Features and content may be added, changed, suspended, or withdrawn as the Services develop.',
      'Educational content and AI-assisted responses are provided for general learning support. They may contain errors or omissions and are not a substitute for a teacher’s guidance, professional advice, official examination rules, or independent verification. Check important answers and instructions against authoritative sources.',
    ],
  },
  {
    title: '4. Laboratory simulations and safety',
    paragraphs: [
      'Virtual practicals are educational simulations and do not replace real laboratory instruction, supervision, or safety procedures. Simulated outcomes may differ from real equipment, materials, or experimental conditions.',
      'Do not perform a physical experiment based only on a simulation. Follow your school’s safety rules, use appropriate protective equipment, and work under qualified supervision. You are responsible for safe conduct when using any real-world materials or equipment.',
    ],
  },
  {
    title: '5. Assessments and examination integrity',
    paragraphs: [
      'Use mock tests and assessments honestly. Do not impersonate another person, obtain or distribute restricted answers, interfere with an assessment, evade stated proctoring or security controls, or help another person do so. Follow any additional instructions shown for a specific examination.',
      'Where an assessment uses integrity, monitoring, or proctoring features, those features and their applicable notices govern the information collected during that assessment. We may review suspected violations and take proportionate action, subject to applicable law and our Privacy Policy.',
    ],
  },
  {
    title: '6. Acceptable use',
    paragraphs: [
      'You may not use the Services to break the law, infringe another person’s rights, upload malicious code, disrupt or probe systems without authorization, scrape or harvest data at scale, reverse engineer the Services except where the law permits, or bypass access restrictions.',
      'Do not upload content that is unlawful, threatening, deceptive, abusive, discriminatory, or that violates another person’s privacy or intellectual-property rights. Do not submit confidential or sensitive information unless a feature specifically requests it and you are authorized to provide it.',
    ],
  },
  {
    title: '7. Your content and intellectual property',
    paragraphs: [
      'You retain rights you hold in material you submit. You give us permission to host, store, reproduce, display, and process that material as reasonably necessary to provide, secure, and improve the Services, including making it available to the school, parent, or class that is authorized to receive it. This permission ends when the material is deleted, except where copies must be retained for legal, security, backup, or recordkeeping purposes.',
      'The Services, including their software, design, branding, and content supplied by A3 or its licensors, are protected by intellectual-property laws. You may use them for personal learning or authorized educational purposes; you may not copy, resell, redistribute, or create a competing service from them without permission, except as allowed by law.',
    ],
  },
  {
    title: '8. Schools, parents, and shared records',
    paragraphs: [
      'School and parent features may make student information, submitted work, attendance, grades, or progress visible to authorized school personnel or linked parents and guardians. Access depends on the account relationship, permissions, and configuration of the relevant feature. Users should contact their school about school-managed records and permissions.',
    ],
  },
  {
    title: '9. Subscriptions and payments',
    paragraphs: [
      'Some features may require a paid subscription or one-time payment. Applicable prices, billing intervals, included features, and payment conditions are presented during purchase and form part of that transaction. Payments may be processed by a third-party provider under its own terms.',
      'Unless a purchase page or applicable law states otherwise, access may depend on successful payment and an active subscription. Refunds, renewals, cancellations, and taxes are governed by the terms shown at purchase and applicable law. Contact us or the named payment provider for transaction questions.',
    ],
  },
  {
    title: '10. Third-party services',
    paragraphs: [
      'The Services may link to or rely on third-party websites, identity providers, payment processors, communications tools, or infrastructure. Those services are operated independently and may have their own terms and privacy practices. We are not responsible for third-party services that we do not control.',
    ],
  },
  {
    title: '11. Suspension and termination',
    paragraphs: [
      'You may stop using the Services at any time. We may restrict, suspend, or terminate access if reasonably necessary to protect users, comply with law, address a security risk, enforce these Terms, or respond to misuse. Where practical and lawful, we will provide notice and an opportunity to resolve the issue.',
      'Provisions that by their nature should continue after access ends, including intellectual-property, disclaimer, liability, and dispute provisions, remain in effect.',
    ],
  },
  {
    title: '12. Disclaimers and liability',
    paragraphs: [
      'To the extent permitted by law, the Services are provided “as is” and “as available”. We do not guarantee uninterrupted or error-free operation, that content will meet every user’s requirements, or that simulated or AI-generated results will be accurate in every case.',
      'Nothing in these Terms excludes rights or liability that cannot legally be excluded. To the extent permitted by law, A3 is not liable for indirect or consequential loss arising from use of the Services. Any liability that cannot be excluded is limited to the amount permitted under applicable law.',
    ],
  },
  {
    title: '13. Privacy',
    paragraphs: [
      'Our Privacy Policy explains how personal information is collected, used, shared, retained, and protected. Read it at',
    ],
  },
  {
    title: '14. Changes to these Terms',
    paragraphs: [
      'We may revise these Terms when the Services, our practices, or applicable law changes. We will post the updated Terms with a revised effective date. Material changes will be communicated through the Services or another appropriate channel when required. Continued use after an update takes effect means you accept the revised Terms.',
    ],
  },
  {
    title: '15. Contact',
    paragraphs: [
      'Questions about these Terms can be sent to contact@alchemistresearch.com. If you are a student using a school-managed account, you may also contact your school administrator about school-specific permissions and records.',
    ],
  },
];

const Terms: React.FC = () => (
  <Box sx={{ minHeight: '100vh', py: { xs: 4, md: 7 }, backgroundColor: 'background.default' }}>
    <Container maxWidth="md">
      <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 800, letterSpacing: 1.3 }}>
          A3 RESEARCH · A3 LEARNING
        </Typography>
        <Typography variant="h3" component="h1" sx={{ mt: 1, mb: 2, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700 }}>
          Terms of Service
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Effective date: September 29, 2026
        </Typography>

        <Stack spacing={3} divider={<Divider flexItem />}>
          {termsSections.map((section) => (
            <Box key={section.title}>
              <Typography variant="h6" component="h2" gutterBottom>{section.title}</Typography>
              {section.paragraphs.map((paragraph) => (
                <Typography key={paragraph} color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                  {paragraph}{section.title === '13. Privacy' && paragraph.endsWith('at') && (
                    <> <Link component={RouterLink} to="/privacy">the Privacy Policy</Link>.</>
                  )}
                </Typography>
              ))}
            </Box>
          ))}
        </Stack>

        <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Link component={RouterLink} to="/privacy" underline="hover">Privacy Policy</Link>
          <Link component={RouterLink} to="/" underline="hover">Return to home</Link>
        </Box>
      </Paper>
    </Container>
  </Box>
);

export default Terms;
